import { Button } from "@mantine/core";
import type { IAccessPopulated } from "@mcapps/shared";
import { IMeeting } from "@mctypes/meeting.types";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react"; // AG Grid Component
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { usePostProjectByProjectIdMeetingMutation } from "../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { Avatar, MCLoader } from "../../../../components";
import { AccessView, generateAccessText } from "../../../../components/AccessComp/AccessComp";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { AccessLevelsEnum } from "@mcapps/shared/enums";
import { getProjectIdFromUrl } from "../../../utils";
import { formatShortDate } from "../../admin/selectors";
import { setMeetingAllFiltersInRedux } from "../meetingSlice";
import { getRefId, MeetingListItem } from "../meetingApiTypes";
import "./MeetingBoardsTable.css";

const MEETING_BOARD_CACHE_KEY = "meeting-board-table";
let lastMeetingBoardRequestKey: string | null = null;

const defaultMeetingSerieAccess: IAccessPopulated = {
  level: AccessLevelsEnum.PROJECT,
  groups: [],
  users: [],
};

const getMeetingSerieAccess = (meeting: MeetingListItem | undefined): IAccessPopulated => {
  const meetingSerie = meeting?.meetingSerie;

  if (meetingSerie == null || typeof meetingSerie !== "object" || !("access" in meetingSerie)) {
    return defaultMeetingSerieAccess;
  }

  const access = meetingSerie.access;

  if (access == null) {
    return defaultMeetingSerieAccess;
  }

  return {
    level: access.level,
    groups: access.groups,
    users: (access.users ?? []).map((user) =>
      typeof user === "string" ? { _id: user, name: "" } : { _id: user._id, name: user.name },
    ),
  };
};

export const meetingBoardColumnDefs: ColDef<MeetingListItem>[] = [
  {
    headerName: "Kommende møter",
    field: "dateOfMeeting",
    hide: true,
  },
  {
    headerName: "ID",
    field: "id",
    sortable: true,
    cellClass: "px-1",
    width: 65,
    cellRenderer: (params) => (
      <Button color="mc-primary" variant="subtle" px={"xs"} size="sm">
        <b>{params.value}</b>
      </Button>
    ),
  },
  {
    headerName: "Tittel",
    field: "name",
    sortable: true,
    filter: "agTextColumnFilter",
    flex: 3,
  },
  {
    headerName: "Møteserie",
    field: "meetingSerie.name",
    sortable: true,
    filter: "agTextColumnFilter",
    flex: 2,
  },
  {
    headerName: "Tilgang",
    field: "meetingSerie.access",
    sortable: true,
    filter: "agSetColumnFilter",
    cellClass: "px-1",
    minWidth: 145,
    valueGetter: (params) => getMeetingSerieAccess(params.data),
    valueFormatter: ({ value }) => generateAccessText(value, "sm"),
    cellRenderer: (params) => <div>{params.value != null && <AccessView access={params.value} variant="sm" />}</div>,
  },
  {
    headerName: "Dato",
    field: "dateOfMeeting",
    sortable: true,
    sort: "desc",
    filter: "agDateColumnFilter",
    // width: 125,
    cellRenderer: (params) => formatShortDate(params.value),
  },
  {
    headerName: "Eier",
    field: "owner",
    sortable: true,
    flex: 1,
    cellRenderer: (params) => {
      const owner = params.value;
      return owner != null && typeof owner !== "string" ? (
        <div className="d-flex align-items-center">
          <Avatar id={owner._id} name={owner.name} />
          <div className="ml-2">{owner.name}</div>
        </div>
      ) : (
        <div></div>
      );
    },
  },
  { headerName: "Jeg er medlem", field: "isMember", hide: true },
  {
    headerName: "Jeg er ansvarlig",
    field: "owner.name",
    hide: true,
  },
];
const MeetingBoardsTable: React.FC<{
  meetingSerie?: IMeeting.MeetingSerie;
  singleMeetingId?: string;
  hideWhenEmpty?: boolean;
  autoHeight?: boolean;
  openMeetingInNewTab?: boolean;
}> = ({ meetingSerie, singleMeetingId, hideWhenEmpty = true, autoHeight = false, openMeetingInNewTab = false }) => {
  const project = useAppSelector((state) => state.adminReducer.project);
  const allMeetingFilters = useAppSelector((state) => state.meetingReducer.allFilters);
  const meetingListRefreshKey = useAppSelector((state) => state.meetingReducer.meetingListRefreshKey);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const [getMeetingInstances, { data, isLoading }] = usePostProjectByProjectIdMeetingMutation({
    fixedCacheKey: MEETING_BOARD_CACHE_KEY,
  });
  const allMeetingFiltersKey = JSON.stringify(allMeetingFilters ?? []);
  const meetings = data?.meetings;
  const requestFilters = React.useMemo(
    () =>
      (allMeetingFilters ?? [])
        .filter((filter) => filter.dataField != null)
        .map((filter) => ({
          dataField: filter.dataField,
          bool: filter.bool,
          options: filter.options?.map((option) => ({
            label: option.label,
            value: option.value,
            isActive: option.isActive,
          })),
        })),
    [allMeetingFilters, allMeetingFiltersKey],
  );
  const meetingBoardRequestKey = JSON.stringify({
    projectId,
    allMeetingFiltersKey,
    meetingListRefreshKey,
  });

  React.useEffect(() => {
    if (!projectId) {
      return;
    }

    if (lastMeetingBoardRequestKey === meetingBoardRequestKey && (data != null || isLoading === true)) {
      return;
    }

    lastMeetingBoardRequestKey = meetingBoardRequestKey;
    getMeetingInstances({
      projectId,
      body: {
        allFilters: requestFilters,
      },
    });
  }, [data, getMeetingInstances, isLoading, meetingBoardRequestKey, projectId, requestFilters]);

  React.useEffect(() => {
    if (data?.allFilters != null) {
      dispatch(setMeetingAllFiltersInRedux(data.allFilters));
    }
  }, [data?.allFilters, dispatch]);
  const currentMeetings: MeetingListItem[] = useMemo(() => {
    if (meetings != null && singleMeetingId != null) {
      return meetings?.reduce((acc, meeting) => {
        if (meeting.isDeleted === true || meeting.meetingSerie == null) {
          return acc;
        }

        const id = getRefId(meeting.meetingSerie);

        if (id != null && id === singleMeetingId) {
          acc.push(meeting);
        }

        return acc;
      }, []);
    } else if (meetings != null && meetingSerie != null) {
      return meetings?.filter((meeting) => getRefId(meeting.meetingSerie) === meetingSerie?._id);
    } else if (meetings != null) {
      return meetings.filter((meeting) => meeting.isDeleted !== true);
    } else {
      return [];
    }
  }, [meetings, meetingSerie, singleMeetingId]);

  const goToMeeting = async (meetingId: string) => {
    if (openMeetingInNewTab === true) {
      window.open(`#/project/${project._id}/meetings/${meetingId}`, "_blank");
      return;
    } else {
      history.push(`/project/${project._id}/meetings/${meetingId}`);
    }
  };

  return hideWhenEmpty === true && (currentMeetings == null || currentMeetings.length === 0) && isLoading === false ? (
    <>Ingen møter tilgjengelig</>
  ) : (
    <div className="meetingTable ag-theme-quartz h-100">
      {isLoading && <MCLoader />}
      <AgGridReact
        domLayout={autoHeight === true ? "autoHeight" : undefined}
        rowData={currentMeetings}
        defaultColDef={{ suppressHeaderMenuButton: true }}
        columnDefs={meetingBoardColumnDefs}
        suppressClickEdit={true}
        rowSelection={{ mode: "singleRow", checkboxes: false }}
        animateRows={true}
        suppressContextMenu={true}
        onGridReady={(e) => {
          e.api.autoSizeColumns(["id", "name"]);
        }}
        onRowClicked={(e) => {
          e.api.deselectAll();
          if (e.data?._id != null) {
            goToMeeting(e.data._id);
          }
        }}
        localeText={AG_GRID_LOCALE_NOR}
      />
    </div>
  );
};

export default MeetingBoardsTable;
